/**
 * Session Cache Utility
 * Manages candidate profile data in sessionStorage for efficient data reuse across screens
 */

import { CandidateProfile } from '../services/candidateServiceApi';

const CACHE_KEY = 'candidate_profile';
const CACHE_TIMESTAMP_KEY = 'candidate_profile_timestamp';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

export class SessionCache {
    /**
     * Store candidate profile in sessionStorage
     */
    static setCandidateProfile(profile: CandidateProfile): void {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(profile));
            sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
            console.log('Candidate profile cached successfully');
        } catch (error) {
            console.error('Failed to cache candidate profile:', error);
        }
    }

    /**
     * Get candidate profile from sessionStorage
     * Returns null if cache is expired or doesn't exist
     */
    static getCandidateProfile(): CandidateProfile | null {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);

            if (!cached || !timestamp) {
                return null;
            }

            // Check if cache is expired
            const cacheAge = Date.now() - parseInt(timestamp);
            if (cacheAge > CACHE_DURATION) {
                console.log('Cache expired, clearing...');
                this.clearCandidateProfile();
                return null;
            }

            return JSON.parse(cached) as CandidateProfile;
        } catch (error) {
            console.error('Failed to retrieve cached profile:', error);
            return null;
        }
    }

    /**
     * Check if candidate profile exists in cache and is valid
     */
    static hasCachedProfile(): boolean {
        const profile = this.getCandidateProfile();
        return profile !== null;
    }

    /**
     * Clear candidate profile from sessionStorage
     */
    static clearCandidateProfile(): void {
        try {
            sessionStorage.removeItem(CACHE_KEY);
            sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
            console.log('Candidate profile cache cleared');
        } catch (error) {
            console.error('Failed to clear cache:', error);
        }
    }

    /**
     * Update specific fields in the cached profile
     */
    static updateCandidateProfile(updates: Partial<CandidateProfile>): void {
        try {
            const cached = this.getCandidateProfile();
            if (cached) {
                const updated = { ...cached, ...updates };
                this.setCandidateProfile(updated);
                console.log('Candidate profile updated in cache');
            }
        } catch (error) {
            console.error('Failed to update cached profile:', error);
        }
    }

    /**
     * Get cache age in seconds
     */
    static getCacheAge(): number | null {
        try {
            const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
            if (!timestamp) return null;

            return Math.floor((Date.now() - parseInt(timestamp)) / 1000);
        } catch (error) {
            console.error('Failed to get cache age:', error);
            return null;
        }
    }
}
